/**
 * PDF report labels — EN + VI.
 *
 * The synoptic PDF reads the selected locale from the user's
 * profile (persisted in localStorage) and swaps every label /
 * section heading / date format to match. Values entered by the
 * reader (diagnosis text, specimen description) stay in whatever
 * language they were typed in — we only localise the CHROME
 * (column headers, section titles, signature block, footer).
 *
 * Vietnamese strings reviewed against common Vietnamese histo-
 * pathology reporting conventions — "Bệnh phẩm", "Chẩn đoán",
 * "Mô bệnh học", "Nhận xét". Feel free to refine per local
 * institutional practice by editing this file.
 */
import type { Language } from "./profile";

export interface PdfLabels {
  title:           string;
  status_draft:    string;
  status_finalized: string;
  status_amended:  string;

  patient:         string;
  case_id:         string;
  study_uid:       string;
  report_date:     string;

  specimen:        string;
  laterality:      string;
  clinical_history: string;
  gross:           string;
  microscopic:     string;
  tumour_section:  string;
  histology_type:  string;
  grade:           string;
  tumour_size:     string;
  invasion_depth:  string;
  nodes:           string;
  margins:         string;
  lvi:             string;
  pni:             string;
  ihc_line:        string;
  molecular:       string;
  gleason:         string;

  diagnosis:       string;
  comment:         string;

  annotations:     string;
  illustrations:   string;
  zoom:            string;
  icd10:           string;

  signed_on:       string;
  generated_on:    string;
  reporting_pathologist: string;
  footer_line:     string;
}

const EN: PdfLabels = {
  title:            "Surgical Pathology Report",
  status_draft:     "DRAFT",
  status_finalized: "FINALIZED",
  status_amended:   "AMENDED",

  patient:          "Patient",
  case_id:          "Case ID",
  study_uid:        "Study UID",
  report_date:      "Report date",

  specimen:         "Specimen",
  laterality:       "Laterality",
  clinical_history: "Clinical history",
  gross:            "Gross / macroscopic description",
  microscopic:      "Microscopic description",
  tumour_section:   "Tumour characteristics",
  histology_type:   "Histologic type",
  grade:            "Grade",
  tumour_size:      "Tumour size (mm)",
  invasion_depth:   "Depth of invasion / pT",
  nodes:            "Nodes (positive / examined)",
  margins:          "Margins",
  lvi:              "Lymphovascular invasion",
  pni:              "Perineural invasion",
  ihc_line:         "ER / PR / HER2 / Ki-67",
  molecular:        "Molecular",
  gleason:          "Gleason",

  diagnosis:        "Diagnosis",
  comment:          "Comment",

  annotations:      "ROI annotations",
  illustrations:    "Illustrative views",
  zoom:             "zoom",
  icd10:            "ICD-10-CM diagnosis codes",

  signed_on:        "Signed",
  generated_on:     "Report generated",
  reporting_pathologist: "Reporting pathologist",
  footer_line:      "Generated by VaultPod Pathology Viewer",
};

const VI: PdfLabels = {
  title:            "Báo cáo Giải phẫu bệnh",
  status_draft:     "BẢN NHÁP",
  status_finalized: "HOÀN TẤT",
  status_amended:   "ĐÃ CHỈNH SỬA",

  patient:          "Bệnh nhân",
  case_id:          "Mã hồ sơ",
  study_uid:        "Mã nghiên cứu",
  report_date:      "Ngày báo cáo",

  specimen:         "Bệnh phẩm",
  laterality:       "Bên",
  clinical_history: "Tiền sử lâm sàng",
  gross:            "Mô tả đại thể",
  microscopic:      "Mô tả vi thể",
  tumour_section:   "Đặc điểm khối u",
  histology_type:   "Loại mô học",
  grade:            "Độ biệt hoá",
  tumour_size:      "Kích thước u (mm)",
  invasion_depth:   "Độ xâm lấn / pT",
  nodes:            "Hạch (dương / đã xét)",
  margins:          "Diện cắt",
  lvi:              "Xâm nhập mạch lympho",
  pni:              "Xâm nhập thần kinh",
  ihc_line:         "ER / PR / HER2 / Ki-67",
  molecular:        "Sinh học phân tử",
  gleason:          "Gleason",

  diagnosis:        "Chẩn đoán",
  comment:          "Nhận xét",

  annotations:      "Vùng đánh dấu (ROI)",
  illustrations:    "Hình minh hoạ",
  zoom:             "phóng đại",
  icd10:            "Mã chẩn đoán ICD-10",

  signed_on:        "Đã ký",
  generated_on:     "Báo cáo lập",
  reporting_pathologist: "Bác sĩ giải phẫu bệnh",
  footer_line:      "Tạo bởi VaultPod Pathology Viewer",
};

export function pdfLabels(lang: Language): PdfLabels {
  return lang === "vi" ? VI : EN;
}

/** Vietnamese date format uses "d MMM yyyy" too, with localized
 *  month abbreviations. Intl.DateTimeFormat handles both with a
 *  locale tag. */
export function fmtDatePdf(iso: string | null | undefined, lang: Language): string {
  if (!iso) return "";
  const d = new Date(iso);
  const tag = lang === "vi" ? "vi-VN" : "en-US";
  return d.toLocaleString(tag, { dateStyle: "medium", timeStyle: "short" });
}
